import type { TableHeadCellProps as OriginalTableHeadCellProps } from 'src/components/table';

import {
  Box,
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  CardContent,
  TableContainer,
} from '@mui/material';

type TableHeadCellProps = OriginalTableHeadCellProps & { dataTestId?: string };

import { useParams } from 'react-router';

import { useComplianceHistoryByPolicyId } from 'src/api/hooks/usePolicyDetailsQueries';
import { ComplianceNotificationTypeEnum } from 'src/api/types/ComplianceNotificationTypeEnum';

import { useTable, TableNoData, getComparator, TableHeadCustom } from 'src/components/table';

import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import { formatDate, formatDollarAmount } from '../../shared/utils/policyUtils';

const TABLE_HEAD: TableHeadCellProps[] = [
  { id: 'recordId', label: 'Type', dataTestId: 'table-head-compliance-type' },
  { id: 'computedReason', label: 'Reason', dataTestId: 'table-head-reason' },
  { id: 'computedEffectiveDate', label: 'Effective date', dataTestId: 'table-head-effective-date' },
  { id: 'createdDate', label: 'Received date', dataTestId: 'table-head-received-date' },
  {
    id: 'currentOutstandingPremiumDue',
    label: 'Outstanding premium',
    dataTestId: 'table-head-outstanding-premium',
  },
];

function ComplianceHistoryTable() {
  const { comboId, coverageId, policyId } = useParams<{
    comboId: string;
    coverageId: string;
    policyId: string;
  }>();
  const { data, error } = useComplianceHistoryByPolicyId(
    Number(comboId),
    Number(coverageId),
    Number(policyId)
  );

  // Determine no data state depending on if endpoint returns a 404 not found or an empty array
  // This is to handle cases where the policy exists but has no compliance history
  // or the policy does not exist at all.
  const notFound = error?.status === 404 || (Array.isArray(data?.data) && data.data.length === 0);

  const table = useTable({ defaultOrder: 'desc', defaultOrderBy: 'computedEffectiveDate' });

  let tableData = data?.data?.map((item) => ({
    ...item,
    computedReason: item.noncomplianceReasonCode
      ? item.noncomplianceReasonDescription
      : item.complianceReasonDescription,
    computedEffectiveDate: item.noncomplianceReasonCode
      ? item.noncomplianceEffectiveDate
      : item.complianceEffectiveDate,
  }));

  const comparator = getComparator(table.order, table.orderBy);

  const stabilizedData = tableData?.map((el, index) => [el, index] as const);

  stabilizedData?.sort((a: any, b: any) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedData != undefined ? stabilizedData.map((el) => el[0]) : undefined;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: 'text.primary' }}
        data-testid="compliance-history-title"
      >
        Compliance history
      </Typography>
      <TableContainer
        sx={{
          mt: 1.5,
          border: '1px solid var(--palette-divider)',
          borderRadius: 2,
          borderBottom: '1px dashed var(--palette-divider)',
        }}
      >
        <Table sx={{ minWidth: 500 }} data-testid="compliance-history-table">
          <TableHeadCustom
            order={table.order}
            orderBy={table.orderBy}
            headCells={TABLE_HEAD}
            rowCount={data?.data?.length}
            numSelected={table.selected.length}
            onSort={table.onSort}
            data-testid="compliance-history-table-head"
          />
          <TableBody data-testid="compliance-history-table-body">
            {tableData?.map((row, index) => (
              <TableRow
                hover
                tabIndex={-1}
                key={index}
                data-testid={`compliance-history-row-${index}`}
              >
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`compliance-history-type-${index}`}
                >
                  {row.complianceNotificationTypeEnum ===
                  ComplianceNotificationTypeEnum.NOTIF_COMPLIANCE_REASONS
                    ? 'Compliance'
                    : row.complianceNotificationTypeEnum ===
                        ComplianceNotificationTypeEnum.NOTIF_NONCOMPLIANCES
                      ? 'Noncompliance'
                      : 'Unknown'}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`compliance-history-reason-${index}`}
                >
                  {row.computedReason}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'wrap' }}
                  data-testid={`compliance-history-effective-date-${index}`}
                >
                  {formatDate(row.computedEffectiveDate)}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`compliance-history-received-date-${index}`}
                >
                  {formatDate(row.createdDate)}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                  data-testid={`compliance-history-outstanding-premium-${index}`}
                >
                  {formatDollarAmount(row.currentOutstandingPremiumDue)}
                </TableCell>
              </TableRow>
            ))}
            <TableNoData
              notFound={notFound}
              description="This table will be updated if the policy has any compliance issues to report."
              data-testid="compliance-history-no-data"
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function ComplianceHistory() {
  const headerWidth = useHeaderWidth();

  return (
    <Card
      sx={[
        () => ({
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 5,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: headerWidth,
        }),
      ]}
    >
      <CardContent
        sx={{ flexGrow: 1, padding: '0px' }}
        data-testid="policy-complaince-history-card"
      >
        <Box
          sx={{
            paddingTop: '1.5rem',
            paddingRight: '1.5rem',
            paddingLeft: '1.5rem',
            padddingBottom: '2px',
          }}
        >
          <ComplianceHistoryTable />
        </Box>
      </CardContent>
    </Card>
  );
}
